import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  ApiRequest_CreateWithUser_PropertyManager,
  ApiResponse_CreateWithUser_PropertyManager,
  useCreateUserWithPropertyManage,
} from '@/api/endpoints/propertyManager/useCreateUserWithPropertyManage';
import { PropertyManagementRole } from '@/api/enums';

import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';
import { propertyManagerRolesOptions } from '@/components/propertyManager/options';

interface Props {
  onClose: () => void;
  onHouseholdMemberCreated?: (propertyManager: ApiResponse_CreateWithUser_PropertyManager) => void;
  propertyLocationId: number;
}

const InviteHouseholdMemberModal: React.FC<Props> = ({
  onClose,
  onHouseholdMemberCreated,
  propertyLocationId,
}) => {
  const form = useForm<ApiRequest_CreateWithUser_PropertyManager>({
    defaultValues: {
      role: PropertyManagementRole.MEMBER,
      locationId: propertyLocationId,
    },
  });
  const {
    register,
    control,
    formState: { errors },
  } = form;

  // Filter role options to only show Admin, Member, and Viewer
  const inviteRoleOptions = propertyManagerRolesOptions.filter(option =>
    [
      PropertyManagementRole.ADMIN,
      PropertyManagementRole.MEMBER,
      PropertyManagementRole.VIEWER,
    ].includes(option.value as PropertyManagementRole)
  );

  const createUserWithPropertyManageMutation = useCreateUserWithPropertyManage({
    onSuccess(data) {
      onHouseholdMemberCreated?.(data);
      onClose();
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_CreateWithUser_PropertyManager) =>
      createUserWithPropertyManageMutation.mutateAsync(data),
    [createUserWithPropertyManageMutation]
  );

  return (
    <ModalWithForm
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="sm"
      title="Invite User"
      buttonLabel="Send Invitation"
    >
      <FormTextField
        label="Full Name"
        control={control}
        required
        {...register('fullName', { required: 'Full Name is required' })}
        error={!!errors.fullName}
        helperText={errors.fullName?.message?.toString()}
        size="small"
      />

      <FormTextField
        label="Email Address"
        control={control}
        required
        {...register('email', { required: 'Email is required' })}
        error={!!errors.email}
        helperText="They will receive a welcome email with login credentials at this address"
        size="small"
      />

      <FormTextField
        label="Phone Number (Optional)"
        control={control}
        {...register('phone')}
        error={!!errors.phone}
        helperText={errors.phone?.message?.toString()}
        size="small"
      />

      <FormSelect
        label="Role"
        control={control}
        required
        {...register('role', { required: 'Role is required' })}
        options={inviteRoleOptions}
        error={!!errors.role}
        helperText={errors.role?.message?.toString()}
        size="small"
      />
    </ModalWithForm>
  );
};

export default InviteHouseholdMemberModal;
